import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';
import { _Date } from './restaurants-operations-v1-operation-operations.types';

function resolveWixRestaurantsOperationsV1OperationsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/restaurants-operations/v1',
        destPath: '/v1',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/restaurants-operations/v1',
        destPath: '/v1',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/restaurants-operations/v1',
        destPath: '/v1',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/restaurants-operations/v1',
        destPath: '/v1',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/restaurants-operations/v1',
        destPath: '/v1',
      },
      {
        srcPath: '/restaurants/operations/v1/operations',
        destPath: '/v1/operations',
      },
    ],
    _: [
      {
        srcPath: '/_api/restaurants-operations/v1',
        destPath: '/v1',
      },
    ],
    _base_domain_: [
      {
        srcPath: '/_api/restaurants-operations/v1',
        destPath: '/v1',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/restaurants-operations/v1',
        destPath: '/v1',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/restaurants-operations/v1',
        destPath: '/v1',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/restaurants-operations/v1',
        destPath: '/v1',
      },
    ],
    'users._base_domain_': [
      {
        srcPath: '/_api/restaurants-operations/v1',
        destPath: '/v1',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/restaurants-operations',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/restaurants-operations',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

function resolveWixRestaurantsOperationPacingV1OperationPacingServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/rest-operation-pacing',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/rest-operation-pacing',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/rest-operation-pacing',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/rest-operation-pacing',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/rest-operation-pacing',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/rest-operation-pacing',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/rest-operation-pacing',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_restaurants_operations';

/** List Blocked Periods. */
export function listBlockedPeriods(
  payload: object
): RequestOptionsFactory<any> {
  function __listBlockedPeriods({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'from' }, { path: 'until' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operation.pacing.v1.OperationPacingService.ListBlockedPeriods',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationPacingV1OperationPacingServiceUrl({
        protoPath: '/v1/operation-pacing/blocked-periods',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'blockPeriods.from' },
              { path: 'blockPeriods.until' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listBlockedPeriods;
}

/** Retrieves an operation. */
export function getOperation(payload: object): RequestOptionsFactory<any> {
  function __getOperation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn: 'wix.restaurants.operations.v1.OperationsService.GetOperation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/{operationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'operation.createdDate' },
              { path: 'operation.updatedDate' },
              { path: 'operation.pausedUntilOptions.time' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'operation.businessLocationDetails.address.geocode.latitude',
              },
              {
                path: 'operation.businessLocationDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getOperation;
}

/**
 * Updates an operation.
 *
 * If you update part of the `orderScheduling` property, the whole object is overwritten,
 * so you must include the entire object unless you are not updating `orderScheduling` at all. <br />
 *
 * Each time the operation is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the operation.
 * This ensures you're working with the latest operation
 * and prevents unintended overwrites.
 */
export function updateOperation(payload: object): RequestOptionsFactory<any> {
  function __updateOperation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'operation.createdDate' },
          { path: 'operation.updatedDate' },
          { path: 'operation.pausedUntilOptions.time' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'operation.businessLocationDetails.address.geocode.latitude',
          },
          {
            path: 'operation.businessLocationDetails.address.geocode.longitude',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'PATCH' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.UpdateOperation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/{operation.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'operation.createdDate' },
              { path: 'operation.updatedDate' },
              { path: 'operation.pausedUntilOptions.time' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'operation.businessLocationDetails.address.geocode.latitude',
              },
              {
                path: 'operation.businessLocationDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateOperation;
}

/**
 * Deletes an operation.
 * @deprecated It has been replaced with wix.restaurants.v1.OperationGroupService.DeleteOperationGroup(), and will be removed on 2025-08-31.
 */
export function deleteOperation(payload: object): RequestOptionsFactory<any> {
  function __deleteOperation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'DELETE' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.DeleteOperation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/{operationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteOperation;
}

/**
 * Creates a query to retrieve a list of operations.
 *
 * The `queryOperations()` function builds a query to retrieve a list of operations and returns an `OperationsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/operations/operations-query-builder/find) function.
 *
 * You can refine the query by chaining `OperationsQueryBuilder` functions onto the query. `OperationsQueryBuilder` functions enable you to filter, sort, and control the results that `queryOperations()` returns.
 *
 * `queryOperations()` runs with the following `OperationsQueryBuilder` defaults, which you can override:
 *
 * * [`limit(50)`](/operations/operations-query-builder/limit)
 * * [`ascending('entityId')`](/operations/operations-methods-query-builder/ascending)
 *
 * The following `OperationsQueryBuilder` functions are supported for `queryOperations()`. For a full description of the operations object, see the object returned for the [`items`](/operations/operations-query-result/items) property in `OperationsQueryResult`.
 */
export function queryOperation(payload: object): RequestOptionsFactory<any> {
  function __queryOperation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.QueryOperation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'operations.createdDate' },
              { path: 'operations.updatedDate' },
              { path: 'operations.pausedUntilOptions.time' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'operations.businessLocationDetails.address.geocode.latitude',
              },
              {
                path: 'operations.businessLocationDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryOperation;
}

/**
 * Retrieves a list of operations.
 * The result will be sorted by created date in ascending order.
 */
export function listOperations(payload: object): RequestOptionsFactory<any> {
  function __listOperations({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.ListOperations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'operations.createdDate' },
              { path: 'operations.updatedDate' },
              { path: 'operations.pausedUntilOptions.time' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'operations.businessLocationDetails.address.geocode.latitude',
              },
              {
                path: 'operations.businessLocationDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listOperations;
}

/**
 * Retrieves a list of available fulfillment options.
 *
 * What makes a fulfillment option available is whether you can submit an order given the scheduling configurations and the fulfillment method's availability.
 * When a delivery address is not provided in the input, our system retrieves a list encompassing all types of fulfillment methods.
 * Conversely, if a delivery address` is given, the response may includes non-delivery fulfillment options along with delivery fulfillment methods that are applicable to the given address, ensuring the address falls within the defined delivery area of these methods.
 */
export function listAvailableFulfillmentOptions(
  payload: object
): RequestOptionsFactory<any> {
  function __listAvailableFulfillmentOptions({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.ListAvailableFulfillmentOptions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/{operationId}/available-fulfillment-options',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'fulfillmentOptions.pickupOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentOptions.pickupOptions.address.geocode.longitude',
              },
              {
                path: 'fulfillmentOptions.dineInOptions.address.geocode.latitude',
              },
              {
                path: 'fulfillmentOptions.dineInOptions.address.geocode.longitude',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'fulfillmentOptions.availability.startTime' },
              { path: 'fulfillmentOptions.availability.endTime' },
              { path: 'fulfillmentOptions.availability.exceptions.startTime' },
              { path: 'fulfillmentOptions.availability.exceptions.endTime' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listAvailableFulfillmentOptions;
}

/**
 * Retrieves a list of available time slots for each fulfillment type.
 *
 * Each time slot is the first available time slot for the given fulfillment type.
 * @deprecated It has been replaced with wix.restaurants.operations.v1.OperationsService.CalculateFirstAvailableTimeSlotPerFulfillmentType(), and will be removed on 2025-08-31.
 */
export function listFirstAvailableTimeSlotForFulfillmentTypes(
  payload: object
): RequestOptionsFactory<any> {
  function __listFirstAvailableTimeSlotForFulfillmentTypes({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.ListFirstAvailableTimeSlotForFulfillmentTypes',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath:
          '/v1/operations/{operationId}/first-available-time-slot-for-fulfillment-types',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlots.startTime' },
              { path: 'timeSlots.endTime' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'timeSlots.fulfillmentAddress.address.geocode.latitude' },
              {
                path: 'timeSlots.fulfillmentAddress.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listFirstAvailableTimeSlotForFulfillmentTypes;
}

/**
 * Retrieves the first available time slot of each fulfillment type for a specified operation.
 *
 * The response includes a time slot with the delivery fulfillment type only if you specify a delivery address.
 * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
 */
export function calculateFirstAvailableTimeSlotPerFulfillmentType(
  payload: object
): RequestOptionsFactory<any> {
  function __calculateFirstAvailableTimeSlotPerFulfillmentType({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.CalculateFirstAvailableTimeSlotPerFulfillmentType',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath:
          '/v1/operations/{operationId}/first-available-time-slot-per-fulfillment-type',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeslotsPerFulfillmentType.timeSlot.startTime' },
              { path: 'timeslotsPerFulfillmentType.timeSlot.endTime' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'timeslotsPerFulfillmentType.fulfillmentInfo.address.geocode.latitude',
              },
              {
                path: 'timeslotsPerFulfillmentType.fulfillmentInfo.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __calculateFirstAvailableTimeSlotPerFulfillmentType;
}

/**
 * Retrieves a list of available time slots for each fulfillment type.
 *
 * Each time slot is the first available time slot for the given fulfillment type.
 * @deprecated It has been replaced with wix.restaurants.operations.v1.OperationsService.CalculateFirstAvailableTimeSlotsPerOperation(), and will be removed on 2025-08-31.
 */
export function listFirstAvailableTimeSlotsForOperations(
  payload: object
): RequestOptionsFactory<any> {
  function __listFirstAvailableTimeSlotsForOperations({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.ListFirstAvailableTimeSlotsForOperations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/first-available-time-slots-for-operations',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlots.timeSlots.startTime' },
              { path: 'timeSlots.timeSlots.endTime' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'timeSlots.timeSlots.fulfillmentAddress.address.geocode.latitude',
              },
              {
                path: 'timeSlots.timeSlots.fulfillmentAddress.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listFirstAvailableTimeSlotsForOperations;
}

/**
 * Retrieves the first available time slot of each fulfillment type for each specified operation.
 *
 * The response includes time slots with delivery fulfillment types only if you specify a delivery address.
 * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
 */
export function calculateFirstAvailableTimeSlotsPerOperation(
  payload: object
): RequestOptionsFactory<any> {
  function __calculateFirstAvailableTimeSlotsPerOperation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.CalculateFirstAvailableTimeSlotsPerOperation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/first-available-time-slots-per-operations',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              {
                path: 'timeSlotsPerOperation.timeslotsPerFulfillmentType.timeSlot.startTime',
              },
              {
                path: 'timeSlotsPerOperation.timeslotsPerFulfillmentType.timeSlot.endTime',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'timeSlotsPerOperation.timeslotsPerFulfillmentType.fulfillmentInfo.address.geocode.latitude',
              },
              {
                path: 'timeSlotsPerOperation.timeslotsPerFulfillmentType.fulfillmentInfo.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __calculateFirstAvailableTimeSlotsPerOperation;
}

/**
 * For each menu, retrieves the first available time slots for each fulfillment type.
 * @deprecated It has been replaced with wix.restaurants.operations.v1.OperationsService.CalculateFirstAvailableTimeSlotsPerMenu(), and will be removed on 2025-08-31.
 */
export function listFirstAvailableTimeSlotsForMenus(
  payload: object
): RequestOptionsFactory<any> {
  function __listFirstAvailableTimeSlotsForMenus({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.ListFirstAvailableTimeSlotsForMenus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath:
          '/v1/operations/{operationId}/first-available-time-slots-for-menus',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              {
                path: 'timeSlotsPerMenu.timeslotsPerFulfillmentType.startTime',
              },
              { path: 'timeSlotsPerMenu.timeslotsPerFulfillmentType.endTime' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'timeSlotsPerMenu.timeslotsPerFulfillmentType.fulfillmentAddress.address.geocode.latitude',
              },
              {
                path: 'timeSlotsPerMenu.timeslotsPerFulfillmentType.fulfillmentAddress.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listFirstAvailableTimeSlotsForMenus;
}

/**
 * Retrieves the first available time slot of each fulfillment type for each specified menu.
 *
 * The response includes time slots with delivery fulfillment types only if you specify a delivery address.
 * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
 */
export function calculateFirstAvailableTimeSlotsPerMenu(
  payload: object
): RequestOptionsFactory<any> {
  function __calculateFirstAvailableTimeSlotsPerMenu({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.CalculateFirstAvailableTimeSlotsPerMenu',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath:
          '/v1/operations/{operationId}/first-available-time-slots-per-menus',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              {
                path: 'timeSlotsPerMenu.timeslotsPerFulfillmentType.timeSlot.startTime',
              },
              {
                path: 'timeSlotsPerMenu.timeslotsPerFulfillmentType.timeSlot.endTime',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'timeSlotsPerMenu.timeslotsPerFulfillmentType.fulfillmentInfo.address.geocode.latitude',
              },
              {
                path: 'timeSlotsPerMenu.timeslotsPerFulfillmentType.fulfillmentInfo.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __calculateFirstAvailableTimeSlotsPerMenu;
}

/**
 * Retrieves the first available time slot of each fulfillment type for a spastic Item.
 *
 * The response includes time slots with delivery fulfillment types only if you specify a delivery address.
 * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
 */
export function calculateFirstAvailableTimeSlotsForItem(
  payload: object
): RequestOptionsFactory<any> {
  function __calculateFirstAvailableTimeSlotsForItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.CalculateFirstAvailableTimeSlotsForItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath:
          '/v1/operations/{operationId}/first-available-time-slots-per-item',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeslotsPerFulfillmentType.timeSlot.startTime' },
              { path: 'timeslotsPerFulfillmentType.timeSlot.endTime' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'timeslotsPerFulfillmentType.fulfillmentInfo.address.geocode.latitude',
              },
              {
                path: 'timeslotsPerFulfillmentType.fulfillmentInfo.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __calculateFirstAvailableTimeSlotsForItem;
}

/**
 * Retrieves a list of the available time slots for a given date.
 * @deprecated It has been replaced with wix.restaurants.operations.v1.OperationsService.CalculateAvailableTimeSlotsForDate(), and will be removed on 2025-08-31.
 */
export function listAvailableTimeSlotsForDate(
  payload: object
): RequestOptionsFactory<any> {
  function __listAvailableTimeSlotsForDate({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.ListAvailableTimeSlotsForDate',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/{operationId}/available-time-slots-for-date',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlots.startTime' },
              { path: 'timeSlots.endTime' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'timeSlots.fulfillmentAddress.address.geocode.latitude' },
              {
                path: 'timeSlots.fulfillmentAddress.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listAvailableTimeSlotsForDate;
}

/**
 * Retrieves all available time slots of each fulfillment type for the specified date.
 *
 * The response includes time slots with delivery fulfillment types only if you specify a delivery address.
 * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
 */
export function calculateAvailableTimeSlotsForDate(
  payload: object
): RequestOptionsFactory<any> {
  function __calculateAvailableTimeSlotsForDate({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.CalculateAvailableTimeSlotsForDate',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/{operationId}/available-time-slots-per-date',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeslotsPerFulfillmentType.timeSlot.startTime' },
              { path: 'timeslotsPerFulfillmentType.timeSlot.endTime' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'timeslotsPerFulfillmentType.fulfillmentInfo.address.geocode.latitude',
              },
              {
                path: 'timeslotsPerFulfillmentType.fulfillmentInfo.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __calculateAvailableTimeSlotsForDate;
}

/**
 * Retrieves a list of the available dates in a given time range.
 *
 * A date is considered available if it has at least one available time slot.
 * @deprecated It has been replaced with wix.restaurants.operations.v1.OperationsService.CalculateAvailableDatesInRange(), and will be removed on 2025-08-31.
 */
export function listAvailableDatesInRange(
  payload: object
): RequestOptionsFactory<any> {
  function __listAvailableDatesInRange({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.ListAvailableDatesInRange',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/{operationId}/available-dates-in-range',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
    };

    return metadata;
  }

  return __listAvailableDatesInRange;
}

/**
 * Retrieves all available dates of each fulfillment type within the specified time range.
 *
 * A date is considered available if it has at least one available time slot.
 *
 * The response includes time slots with delivery fulfillment types only if you specify a delivery address.
 * Only fulfillment methods capable of delivering to the specified address are considered when calculating time slot availability.
 */
export function calculateAvailableDatesInRange(
  payload: object
): RequestOptionsFactory<any> {
  function __calculateAvailableDatesInRange({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'deliveryAddress.geocode.latitude' },
          { path: 'deliveryAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.CalculateAvailableDatesInRange',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/{operationId}/available-dates',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
    };

    return metadata;
  }

  return __calculateAvailableDatesInRange;
}

/**
 * Validates an operation's address.
 *
 * The method checks if the operation's address stored in `businessLocationDetails.address` is valid.
 *
 * An address is considered valid if it has:
 *
 * 1. A non-empty, formatted address.
 * 2. Valid geocode coordinates (not 0,0).
 * 3. A non-empty country field.
 *
 * If the address is invalid, the response includes specific violations.
 */
export function validateOperationAddress(
  payload: object
): RequestOptionsFactory<any> {
  function __validateOperationAddress({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.ValidateOperationAddress',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/operations/{operationId}/validate-address',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __validateOperationAddress;
}

/**
 * Synchronously update tags on multiple operations.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 */
export function bulkUpdateOperationTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateOperationTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.BulkUpdateOperationTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/bulk/operations/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateOperationTags;
}

/**
 * Asynchronously update tags on multiple operations according to the specified filter.
 * If a filter isn't specified, this method updates all operations.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 */
export function bulkUpdateOperationTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateOperationTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.operations.v1.operation',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.operations.v1.OperationsService.BulkUpdateOperationTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsOperationsV1OperationsServiceUrl({
        protoPath: '/v1/bulk/operations/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateOperationTagsByFilter;
}
